import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Ticket, Trophy } from 'lucide-react';
import { API_BASE } from '../ton/config';
import { formatTon } from '../lib/format';

interface FeedItem {
  kind: 'buy' | 'win';
  address: string;
  amountTon: number;
  utime: number;
}

interface TxRow {
  kind: string;
  src: string;
  value_nano: string;
  utime: string;
  success: boolean;
}

const ROTATE_MS = 3200;

function short(addr: string): string {
  return addr.length > 10 ? `${addr.slice(0, 4)}…${addr.slice(-4)}` : addr;
}

function useAgo(): (utime: number) => string {
  const { t } = useTranslation();
  return (utime: number) => {
    const s = Math.max(0, Math.floor(Date.now() / 1000) - utime);
    if (s < 60) return t('v2.feed.now');
    if (s < 3600) return t('v2.feed.minAgo', { n: Math.floor(s / 60) });
    if (s < 86400) return t('v2.feed.hourAgo', { n: Math.floor(s / 3600) });
    return t('v2.feed.dayAgo', { n: Math.floor(s / 86400) });
  };
}

/** Live on-chain activity: real ticket buys from the indexer, rotating one row
 *  at a time. Social proof that the game is alive right now. */
export function LiveFeed({ lastWin }: { lastWin: { address: string | null; amount: number; utime: number } }) {
  const { t } = useTranslation();
  const ago = useAgo();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!API_BASE) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/transactions?limit=40`);
        if (!res.ok) return;
        const data = (await res.json()) as { items?: TxRow[] };
        if (cancelled || !data.items) return;
        const buys = data.items
          .filter((i) => i.kind === 'buy' && i.success)
          .map<FeedItem>((i) => ({
            kind: 'buy',
            address: i.src,
            amountTon: Number(i.value_nano) / 1e9,
            utime: Number(i.utime),
          }));
        setItems(buys.slice(0, 16));
      } catch {
        /* feed is decorative — stay silent */
      }
    };
    void load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const rows = useMemo<FeedItem[]>(() => {
    const all = [...items];
    if (lastWin.address && lastWin.amount > 0) {
      all.unshift({ kind: 'win', address: lastWin.address, amountTon: lastWin.amount, utime: lastWin.utime });
    }
    return all;
  }, [items, lastWin]);

  useEffect(() => {
    if (rows.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % rows.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [rows.length]);

  if (rows.length === 0) return null;
  const row = rows[idx % rows.length];

  return (
    <div className="lfeed" aria-live="off">
      <span className="lfeed__pulse" />
      <AnimatePresence mode="wait">
        <motion.span
          key={`${row.kind}-${row.utime}-${idx}`}
          className="lfeed__row"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
        >
          {row.kind === 'win' ? (
            <>
              <Trophy size={13} className="lfeed__ico lfeed__ico--win" aria-hidden="true" />
              <b>{short(row.address)}</b> {t('v2.feed.won')}{' '}
              <b className="lfeed__amt">+{formatTon(row.amountTon)} GRAM</b>
            </>
          ) : (
            <>
              <Ticket size={13} className="lfeed__ico" aria-hidden="true" />
              <b>{short(row.address)}</b> {t('v2.feed.bought')}{' '}
              <b className="lfeed__amt">{formatTon(row.amountTon)} GRAM</b>
            </>
          )}
          <span className="lfeed__ago">{ago(row.utime)}</span>
        </motion.span>
      </AnimatePresence>
    </div>
  );
}
